import { bindCanvasInvestigation, closeTraceCanvas, openCaseCanvas } from "../lib/traceCanvasStore.js";
// WorkspaceInventory: the Workspaces page as the team's investigations. Each
// row is what is being investigated, by whom, when it was opened and when it was
// last worked on, latest activity first; closed ones on request. A row opens
// its work: the board this browser keeps for it, else a new local board bound to the shared investigation.
import { useCallback, useMemo, useState } from "react";
import ScrollArea from "./ScrollArea.jsx";
import Checkbox from "./ui/Checkbox.jsx";
import { SkeletonRows } from "./ui/Skeleton.jsx";
import { getFindingInvestigation } from "../lib/alertTriageClient.js";
import { showWorkspaceInDeck, startWorkspaceInDeck } from "../lib/deckRequests.js";
import { requestFreshChat } from "./CanvasChat.jsx";
import { buttonClass } from "./ui/Button.jsx";
import { ownerLabel } from "../lib/findingsConsole.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { useHasAnyPermission } from "../lib/permissions.js";
import { bytesBase64ToUuid } from "../lib/reviewClient.js";
import { caseCanvasId, MAX_BOARD_SESSIONS, reopenTraceCanvas, saveTraceCanvas, traceCanvasSnapshot, useTraceCanvases } from "../lib/traceCanvasStore.js";
import { useConsoleInvestigations } from "../lib/useConsoleInvestigations.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { boardOf, listed, onDate, statusWords, when } from "../lib/workspaceInventory.js";

// The reader as the investigation API names users, a UUID: /v1/me may send
// the id's bytes base64-encoded.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asUserId = (id) => (!id ? null : UUID.test(id) ? id.toLowerCase() : bytesBase64ToUuid(id) || id);

const COLS = "grid-cols-[minmax(240px,1fr)_minmax(140px,200px)_104px_128px]";
const COLUMNS = [["title", "Investigation"], ["owner", "Owner"], ["opened", "Opened"], ["activity", "Last activity"]];

// How the reader left the page, found again on coming back from a board.
const left = { closed: false, by: "activity", reverse: false };

// A person as a small disc with their initial: the reader's in the accent,
// a teammate's in gray, nobody's an empty outline.
function Disc({ person }) {
  const tone = !person.id ? "border border-dashed border-[color:var(--line-strong)]"
    : person.mine ? "bg-[color:var(--green-soft)] text-[color:var(--green)]" : "bg-[color:var(--gray-fill)] text-[color:var(--muted)]";
  return <span aria-hidden="true" className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full text-[10px] font-semibold leading-none ${tone}`}>{person.letter}</span>;
}

export default function WorkspaceInventory() {
  const user = useCurrentUser();
  const me = asUserId(user?.userId);
  const canWrite = useHasAnyPermission([PERMISSIONS.ALERT_TRIAGE_WRITE, PERMISSIONS.USERS_MANAGE]);
  const [closed, setClosed] = useState(left.closed);
  const invs = useConsoleInvestigations({ enabled: true, canWrite, status: closed ? "" : "open" });
  const [sort, setSort] = useState({ by: left.by, reverse: left.reverse });
  const [opening, setOpening] = useState(null);
  const [failed, setFailed] = useState(null);
  const workspaces = useTraceCanvases();
  const now = useMemo(() => Date.now(), [invs.byId]);

  const nameOf = useCallback((id) => ownerLabel(id, invs.users, me), [invs.users, me]);
  const personOf = useCallback((id) => {
    if (!id) return { id: null, name: "Unassigned", letter: "" };
    const email = id === me ? user?.email || "" : invs.users.get(id) || "";
    return { id, name: nameOf(id), letter: email.charAt(0).toUpperCase(), mine: id === me };
  }, [invs.users, me, nameOf, user?.email]);
  const list = useMemo(() => listed(invs.byId, { closed, by: sort.by, reverse: sort.reverse, nameOf }), [invs.byId, closed, sort, nameOf]);

  const sortBy = (by) => setSort((s) => {
    const next = { by, reverse: s.by === by ? !s.reverse : false };
    Object.assign(left, next);
    return next;
  });
  const showClosed = (value) => {
    left.closed = value;
    setClosed(value);
  };

  // An investigation's members say which board is its, read when it is
  // opened. With none kept in this browser, its findings (those not set
  // aside, else all) are laid out on a new one, anchored on the first's
  // session; one with no findings opens on its question (its summary, else
  // its title).
  const open = useCallback(async (inv) => {
    if (opening) return;
    setOpening(inv.incidentId);
    setFailed(null);
    try {
      const got = await getFindingInvestigation(inv.incidentId);
      const boards = workspaces.filter((w) => w.id.startsWith("case")).map((w) => ({ id: w.id, investigationId: traceCanvasSnapshot(w.id).investigationId, findings: traceCanvasSnapshot(w.id).findings.map((f) => f.id) }));
      const kept = boardOf(got.findings, boards, inv.incidentId);
      const live = got.findings.filter((f) => !f.setAsideReason);
      const members = live.length ? live : got.findings;
      const board = kept || openCaseCanvas({
        id: caseCanvasId("investigation", inv.incidentId),
        title: inv.title || "Investigation",
        question: members.length ? "" : got.summary || got.title || inv.title || "",
        anchor: members[0]?.trace_id || "",
        sessions: [...new Set(members.map((f) => f.trace_id))].slice(0, MAX_BOARD_SESSIONS).map((id) => ({ id })),
        findings: members.map((f) => ({ id: f.finding_id, session: f.trace_id, title: "" })),
        open: false,
      });
      if (!board) return setFailed(inv.incidentId);
      bindCanvasInvestigation(board, inv.incidentId);
      if (!kept) saveTraceCanvas(board);
      if (!showWorkspaceInDeck(board)) reopenTraceCanvas(board);
    } catch {
      setFailed(inv.incidentId);
    } finally {
      setOpening(null);
    }
  }, [opening, workspaces]);

  const loading = invs.status === "loading";
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col" data-workspace-inventory="">
      <header className="flex shrink-0 flex-wrap items-center gap-x-[20px] gap-y-[8px] pb-[14px] pr-[16px] pt-[7px]">
        {/* The rail names the page; the heading stays for assistive tech. */}
        <h1 className="sr-only">Investigations</h1>
        <button
          type="button"
          onClick={() => { requestFreshChat(); closeTraceCanvas(); startWorkspaceInDeck(); }}
          className={buttonClass("secondary")}
          data-inventory-new=""
        >
          + New investigation
        </button>
        <Checkbox checked={closed} onChange={showClosed} label="Show closed" data-inventory-closed="" />
      </header>
      {invs.capped ? <p className="m-0 shrink-0 pb-[8px] text-[12px] text-[color:var(--muted)]">Showing the newest 1,000 investigations.</p> : null}
      {failed ? <p role="alert" className="m-0 shrink-0 pb-[8px] text-[12.5px] text-[color:var(--severity-critical)]">That investigation could not be opened. Try again.</p> : null}
      <ScrollArea className="min-h-0 flex-1" contentClassName="pb-[96px]">
        <div role="table" aria-label="Investigations" aria-busy={loading ? "true" : undefined}>
          <div role="row" className={`sticky top-0 z-[1] grid ${COLS} items-end gap-x-[16px] border-b border-[color:var(--line)] bg-[color:var(--bg)] pb-[6px] pl-[12px] pr-[16px]`}>
            {COLUMNS.map(([by, label]) => (
              <span key={by} role="columnheader" aria-sort={sort.by === by ? (sort.reverse ? "ascending" : "descending") : "none"}>
                <button type="button" onClick={() => sortBy(by)} className={`flex items-center gap-[5px] text-[12px] ${sort.by === by ? "text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`} data-inventory-sort={by}>
                  {label}
                  {sort.by === by ? <span aria-hidden="true" className="text-[10px]">{sort.reverse ? "↑" : "↓"}</span> : null}
                </button>
              </span>
            ))}
          </div>
          {invs.status === "error" ? <p role="alert" className="m-0 py-[24px] pl-[12px] text-[13px] text-[color:var(--severity-critical)]">The investigations could not be read. Reload to try again.</p>
            : loading ? <SkeletonRows rows={8} cells={["w-[60%]", "w-[55%]", "w-[64px]", "w-[72px]"]} rowClassName={`grid h-[42px] ${COLS} items-center gap-x-[16px] pl-[12px] pr-[16px]`} label="Loading investigations…" />
              : !list.length ? <p className="m-0 py-[24px] pl-[12px] text-[13px] text-[color:var(--muted)]">{closed ? "No investigations yet." : "No open investigations. Turn on Show closed to see closed ones."}</p>
                : list.map((inv) => {
                  const done = inv.status === "closed";
                  const owner = personOf(inv.ownerUserId);
                  return (
                    <button
                      key={inv.incidentId}
                      type="button"
                      role="row"
                      onClick={() => open(inv)}
                      aria-busy={opening === inv.incidentId ? "true" : undefined}
                      className={`grid min-h-[42px] w-full ${COLS} items-center gap-x-[16px] border-b border-[color:var(--line)] py-[8px] pl-[12px] pr-[16px] text-left hover:bg-[color:var(--gray-fill)]`}
                      data-inventory-row={inv.incidentId}
                    >
                      <span role="cell" className="flex min-w-0 items-baseline gap-[10px]">
                        <span className={`truncate text-[13px] ${done ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>{inv.title || "Untitled investigation"}</span>
                        {done ? <span className="shrink-0 text-[12px] text-[color:var(--muted)]">{statusWords(inv)}</span> : null}
                      </span>
                      <span role="cell" className="flex min-w-0 items-center gap-[7px]">
                        <Disc person={owner} />
                        <span className={`truncate text-[12.5px] ${owner.id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>{owner.name}</span>
                      </span>
                      <span role="cell" className="text-[12.5px] tabular-nums text-[color:var(--muted)]">{onDate(inv.createdAtMs, now)}</span>
                      <span role="cell" className="text-[12.5px] tabular-nums text-[color:var(--ink)]">{when(inv.updatedAtMs, now)}</span>
                    </button>
                  );
                })}
        </div>
      </ScrollArea>
    </div>
  );
}
